import { Chip, List, ListItemButton, ListItemText, Stack } from '@mui/material';

import { CaptionText } from '../../shared/typography/PrimaryTypography';
import { EntityCandidate } from '../../../types/syntiro_honey';

interface EntityCandidatesPickerProps {
  /** The ranked entity candidates surfaced by the resolver. */
  candidates: Array<EntityCandidate>;
  /** The entity id currently chosen in the edit form (deepest id), or null. */
  selectedEntityId: string | null;
  /** Called with the picked candidate so the form can set the right FK level. */
  onSelect: (candidate: EntityCandidate) => void;
}

/** The deepest id a candidate represents — its stable selection key. */
function candidateId(candidate: EntityCandidate): string {
  return candidate.siteId ?? candidate.subdivisionId ?? candidate.divisionId ?? candidate.name;
}

/**
 * List of ranked entity candidates (name, org level, score and reason) for a record
 * whose entity hint was ambiguous. The reviewer must pick explicitly — nothing is
 * ever auto-attached.
 * @returns {JSX.Element}
 */
export default function EntityCandidatesPicker({
  candidates,
  selectedEntityId,
  onSelect
}: EntityCandidatesPickerProps) {
  return (
    <List dense disablePadding>
      {candidates.map((candidate) => (
        <ListItemButton
          key={candidateId(candidate)}
          selected={candidateId(candidate) === selectedEntityId}
          onClick={() => onSelect(candidate)}
          sx={{ borderRadius: 1 }}
        >
          <ListItemText
            disableTypography
            primary={
              <Stack direction="row" spacing={1} alignItems="center">
                <CaptionText weight="Medium" sx={{ flex: 1 }}>
                  {candidate.name}
                </CaptionText>
                <Chip label={candidate.kind.toLowerCase()} size="small" variant="outlined" />
                <Chip label={candidate.score.toFixed(2)} size="small" variant="outlined" />
              </Stack>
            }
            secondary={<CaptionText>{candidate.reason}</CaptionText>}
          />
        </ListItemButton>
      ))}
    </List>
  );
}
